import { RegisterUserWizard } from "./RegisterUsersWizard.default";
import {
  getRegisterUsersTemplate,
  registerCompanyUser,
  registerCompanyUsersFile,
  registerForCompanyWithFilePreview,
} from "../repository/auth.repository";
import { retry } from "../utils/http";
import {
  // getCompanies,
  getCompanyDepartments,
  getCompanyUserRoles,
} from "../repository/data.repository";
import { nameOf } from "../utils/typescript";
import {
  // Company,
  CompanyRole,
  Department,
  MAX_PAGE_SIZE,
} from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";
import dayjs from "dayjs";
import DynamicFormWizardActions, {
  WizardStep,
} from "../controls/DynamicFormWizardActions";
import { useTokenInfo } from "../hooks/useTokenInfo";

export interface RegisterUsersWizardProps {
  readonly open?: boolean;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function RegisterUsersWizard(props: RegisterUsersWizardProps) {
  const { claims } = useTokenInfo();

  const userCompnayName = claims.usrCmpName;
  const userCompnayUid = claims.usrCmpUid;

  const companiesRequest = (_search: string) => {
    return Promise.resolve([
      {
        display: userCompnayName,
        value: userCompnayUid,
        dataItem: {
          name: userCompnayName,
          uid: userCompnayUid,
        },
      },
    ]);
  };

  const userRolesRequest = (search: string, formValues: any) => {
    if (!formValues?.company?.value) {
      return Promise.resolve([]);
    }

    return retry(() =>
      getCompanyUserRoles({
        searchQuery: {
          conditions: [
            {
              field: nameOf<CompanyRole>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            {
              field: nameOf<CompanyRole>("companyUid"),
              operation: QueryOperation.Equal,
              values: [formValues.company.value],
            },
            {
              field: nameOf<CompanyRole>("isManager"),
              operation: QueryOperation.Equal,
              values: [true],
            },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then(({ data }) => {
        return data.map((dataItem) => ({
          display: dataItem.name,
          value: dataItem.uid,
          dataItem,
        }));
      })
      .catch((error) => {
        return [];
      });
  };

  const departmentsRequest = (search: string, formValues: any) => {
    if (!formValues?.company?.value) {
      return Promise.resolve([]);
    }

    return retry(() =>
      getCompanyDepartments({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Department>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            {
              field: nameOf<Department>("companyUid"),
              operation: QueryOperation.Equal,
              values: [formValues.company.value],
            },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then(({ data }) => {
        return data.map((dataItem) => ({
          display: dataItem.name,
          value: dataItem.uid,
          dataItem,
        }));
      })
      .catch((error) => {
        return [];
      });
  };

  // Define wizard steps
  const steps: WizardStep[] = [
    {
      title: "Option",
      createSchema: () => ({
        groups: [
          {
            controls: [
              {
                type: "combobox",
                name: "company",
                label: "Company",
                disabled: true,
                rules: [
                  {
                    required: true,
                    message: "Company is required",
                  },
                ],
                request: companiesRequest,
              },
            ],
          },
        ],
      }),
    },
    {
      title: "User",
      createSchema: (values) => {
        return {
          groups: [
            {
              name: "usersInfos",
              label: "User",
              isList: true,
              controls: [
                {
                  type: "textbox",
                  name: "firstName",
                  label: "First Name",
                  rules: [
                    {
                      required: true,
                      message: "First Name is required",
                    },
                  ],
                },
                {
                  type: "textbox",
                  name: "lastName",
                  label: "Last Name",
                  rules: [
                    {
                      required: true,
                      message: "Last Name is required",
                    },
                  ],
                },
                {
                  type: "textbox",
                  name: "email",
                  label: "Email",
                  rules: [
                    {
                      type: "email",
                      message: "Invalid email format",
                    },
                  ],
                },
                {
                  type: "datepicker",
                  name: "dateOfBirth",
                  label: "Date Of Birth",
                  rules: [
                    {
                      required: true,
                      message: "Date Of Birth is required",
                    },
                  ],
                },
                {
                  type: "combobox",
                  name: "role",
                  label: "Role",
                  rules: [
                    {
                      required: true,
                      message: "Role is required",
                    },
                  ],
                  request: (search) => userRolesRequest(search, values),
                },
                {
                  type: "combobox",
                  name: "department",
                  label: "Department",
                  rules: [
                    {
                      required: true,
                      message: "Department is required",
                    },
                  ],
                  request: (search) => departmentsRequest(search, values),
                },
              ],
            },
          ],
        };
      },
    },
    {
      title: "Finish",
    },
  ];

  return (
    <DynamicFormWizardActions<RegisterUserWizard>
      open={props.open}
      title="Register Managers"
      steps={steps}
      onFinish={props.onFinish}
      onCancel={props.onCancel}
      showOptionSelection={true}
      optionSelectionText="Do you want to register users or import an excel file with users?"
      optionLabels={{
        single: "Register",
        multiple: "Import",
      }}
      fileUploadSupport={true}
      fileUploadText="Create or browse an Excel file with users"
      fileUploadButtonText="Browse Excel File"
      downloadTemplateSupport={true}
      downloadTemplateButtonText="Download Template"
      fileUploadSizeLimit={100}
      fileUploadErrorText="Excel size limit is 100mb"
      fileUploadAccept=".xls,.xlsx"
      initialValues={{
        company: {
          display: userCompnayName,
          value: userCompnayUid,
          dataItem: {
            name: userCompnayName,
            uid: userCompnayUid,
          },
        },
        usersInfos: [{} as any],
      }}
      apiCalls={{
        submit: async (values) => {
          const input = {
            companyUid: values.company?.value!,
            usersInfos: values.usersInfos.map(
              ({
                email,
                firstName,
                lastName,
                dateOfBirth,
                role,
                department,
              }) => ({
                email,
                firstName,
                lastName,
                dateOfBirth: dateOfBirth.format("YYYY-MM-DD"),
                roleUid: role.value,
                departmentUid: department.value,
              }),
            ),
          };
          return registerCompanyUser(input);
        },
        submitFile: async (file, values) => {
          const input = {
            companyUid: values.company?.value!,
            file: file,
          };
          return registerCompanyUsersFile(input);
        },
        downloadTemplate: async () => {
          return getRegisterUsersTemplate();
        },
        previewFile: async (file, values) => {
          const { company } = values;
          const result = await registerForCompanyWithFilePreview({
            file,
            companyUid: company?.value!,
          });

          return {
            ...values,
            usersInfos: result.usersInfos.map(
              ({
                email,
                firstName,
                lastName,
                dateOfBirth,
                roleName,
                roleUid,
                departmentName,
                departmentUid,
              }) => {
                return {
                  email,
                  firstName,
                  lastName,
                  dateOfBirth: dayjs(dateOfBirth, ["YYYY-MM-DD"]),
                  role: {
                    display: roleName,
                    value: roleUid,
                  },
                  department: {
                    display: departmentName,
                    value: departmentUid,
                  },
                };
              },
            ),
          };
        },
      }}
      completeButtonText="Complete"
      retryButtonText="Retry"
      closeButtonText="Close"
      resetButtonText="Register Users"
      errorText="Something went wrong, try again"
    />
  );
}

export default RegisterUsersWizard;
